import * as React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Segmented } from '../components/table';

// Renders any app page in iframes at several device sizes, so media queries
// fire at each frame's width. Touch is not emulated.

const DEVICES = [
    { id: 'se', label: 'iPhone SE', w: 375, h: 667 },
    { id: 'i15', label: 'iPhone 15', w: 393, h: 852 },
    { id: 'px', label: 'Pixel 8', w: 412, h: 915 },
    { id: 'slim', label: 'Narrow window', w: 720, h: 900 },
    { id: 'ipad', label: 'iPad mini', w: 744, h: 1133 },
];

const PAGES = [
    { path: '/home', label: 'Home' },
    { path: '/activations', label: 'Activations' },
    { path: '/activations/101', label: 'Event overview (live)' },
    { path: '/activations/101/sharing', label: 'Sharing & Delivery' },
    { path: '/device-management', label: 'Devices' },
    { path: '/pipeline', label: 'Pipeline' },
    { path: '/pipeline/10', label: 'Job — Hoopfest tour' },
    { path: '/pipeline/13', label: 'Job — pending deal' },
    { path: '/staff', label: 'Staff' },
    { path: '/gear', label: 'Gear' },
    { path: '/venues', label: 'Venues' },
    { path: '/contacts', label: 'Contacts' },
    { path: '/org', label: 'Org settings (classic)' },
    { path: '/rules', label: 'Design rules' },
];

const KEY = 'ps.preview';

export default function PreviewV3() {
    const navigate = useNavigate();
    const saved = (() => {
        try {
            return JSON.parse(window.localStorage.getItem(KEY) ?? '{}') ?? {};
        } catch {
            return {};
        }
    })();
    const [path, setPath] = useState<string>(saved.path ?? '/activations');
    const [draft, setDraft] = useState(path);
    const [on, setOn] = useState<string[]>(saved.on ?? ['se', 'i15', 'slim']);
    const [fit, setFit] = useState<'fit' | 'actual'>(saved.fit ?? 'fit');
    const [nonce, setNonce] = useState(0);
    const stage = useRef<HTMLDivElement>(null);
    const [stageH, setStageH] = useState(800);

    useEffect(() => {
        try {
            window.localStorage.setItem(KEY, JSON.stringify({ path, on, fit }));
        } catch {
            /* storage unavailable; settings just won't persist */
        }
    }, [path, on, fit]);

    useLayoutEffect(() => {
        const measure = () => setStageH(stage.current?.clientHeight ?? 800);
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    const devices = DEVICES.filter((d) => on.includes(d.id));
    const go = (p: string) => {
        /* same-site paths only: a leading // would load another site */
        const clean = `/${p.replace(/^\/+/, '')}`;
        setPath(clean);
        setDraft(clean);
    };

    return createPortal(
        <div className="psv3 col pv">
            <header className="pv-bar">
                <button className="crumb" onClick={() => navigate('/rules')}>
                    ← Rules
                </button>
                <strong>Device preview</strong>
                <select
                    className="input select"
                    value={PAGES.some((p) => p.path === path) ? path : ''}
                    onChange={(e) => e.target.value && go(e.target.value)}
                    aria-label="Page"
                >
                    <option value="">Custom path…</option>
                    {PAGES.map((p) => (
                        <option key={p.path} value={p.path}>
                            {p.label}
                        </option>
                    ))}
                </select>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        go(draft.trim() || '/');
                    }}
                >
                    <input
                        className="input"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        aria-label="Path"
                        spellCheck={false}
                    />
                </form>
                <span className="pv-devs" role="group" aria-label="Devices">
                    {DEVICES.map((d) => (
                        <button
                            key={d.id}
                            type="button"
                            aria-pressed={on.includes(d.id)}
                            className={on.includes(d.id) ? 'on' : ''}
                            onClick={() =>
                                setOn((cur) =>
                                    cur.includes(d.id)
                                        ? cur.filter((x) => x !== d.id)
                                        : [...cur, d.id],
                                )
                            }
                        >
                            {d.label}
                        </button>
                    ))}
                </span>
                <Segmented
                    label="Scale"
                    options={[
                        { value: 'fit', label: 'Fit' },
                        { value: 'actual', label: '100%' },
                    ]}
                    value={fit}
                    onChange={setFit}
                />
                <button className="tb-btn" onClick={() => setNonce((n) => n + 1)}>
                    Reload
                </button>
            </header>

            <div className="pv-stage" ref={stage}>
                {devices.length === 0 && (
                    <p className="pg-sub">Pick at least one device.</p>
                )}
                {devices.map((d) => {
                    const scale =
                        fit === 'fit' ? Math.min(1, (stageH - 56) / d.h) : 1;
                    return (
                        <figure key={d.id} className="pv-dev">
                            <figcaption>
                                {d.label} <span>{d.w}×{d.h}</span>
                                {scale < 1 && <span> · {Math.round(scale * 100)}%</span>}
                            </figcaption>
                            <div
                                className="pv-frame"
                                style={{ width: d.w * scale, height: d.h * scale }}
                            >
                                <iframe
                                    key={`${path}-${nonce}`}
                                    title={`${d.label} — ${path}`}
                                    src={path}
                                    style={{
                                        width: d.w,
                                        height: d.h,
                                        transform: `scale(${scale})`,
                                    }}
                                />
                            </div>
                        </figure>
                    );
                })}
            </div>
        </div>,
        document.body,
    );
}
